/**
 * WizardShell — shared wizard chrome (S0.3)
 *
 * Extracted ONCE from the VendorFormWizard skeleton: layout / step nav / footer /
 * navigation guard ONLY. All behaviors (validation trigger() lists, dup-check FSM,
 * cumulative save, hydration/reset) stay in the consuming wizard.
 *
 * Chrome contract (pixel parity with VendorFormWizard):
 * - 300px sticky Sider with hand-rolled step cards (state colors: error / current /
 *   completed / pending) and a "Step n of N" + 4px purple progress footer (80px).
 * - Content area with inner scroll and inset shadow, maxWidth 1400.
 * - 80px fixed footer at left:300 — danger Exit on the left; Previous / Next /
 *   primary Save (edit, every step) or Submit (create, last step) on the right,
 *   all 44px (`controlHeightTokens.controlHeightXL`).
 * - Guard: syncs `hasUnsavedChanges` into NavigationGuardContext (cleared on
 *   unmount), beforeunload listener, and a guarded default Exit handler via
 *   `useUnsavedChangesModal`.
 *
 * All user-visible text is injected via the `texts` prop — consumers translate
 * with their own namespace (i18n keys must exist in BOTH en + zh).
 */

import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout, Badge, Button, Typography, theme } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigationGuard } from '@/contexts/NavigationGuardContext';
import { useUnsavedChangesModal } from '@/components/common/ConfirmModal';
import { controlHeightTokens } from '@/styles/tokens';

const { Sider, Content } = Layout;
const { Text } = Typography;

/** MainLayout navbar height (Ant Design default) */
const NAVBAR_HEIGHT = 48;
/** Sticky bottom action bar height */
const FOOTER_HEIGHT = 80;
/** Step sidebar width */
const SIDER_WIDTH = 300;

export interface WizardStepItem {
  /** Localized step title */
  title: ReactNode;
  /** Localized one-line step description */
  description?: ReactNode;
  /** Step icon rendered in the 48px icon box */
  icon: ReactNode;
}

export interface WizardShellTexts {
  /** Footer left danger button */
  exit: string;
  /** Footer back button (hidden on first step) */
  previous: string;
  /** Footer forward button (hidden on last step) */
  next: string;
  /** Edit-mode primary button, shown on every step (cumulative save) */
  save?: string;
  /** Create-mode primary button, shown on the last step only */
  submit?: string;
  /** Step-card status badge labels */
  status: {
    completed: string;
    inProgress: string;
    pending: string;
    hasErrors: string;
  };
  /** Small uppercase label above each step title. Defaults to VendorFormWizard parity. */
  stepLabel?: (stepNumber: number) => string;
  /** Progress caption in the Sider footer. Defaults to VendorFormWizard parity. */
  progressLabel?: (current: number, total: number) => string;
}

export interface WizardShellProps {
  /** Ordered wizard steps (icon + localized title/description) */
  steps: WizardStepItem[];
  /** Zero-based current step index */
  currentStep: number;
  /**
   * Step navigation intent (step-card click, Previous, Next). The consumer owns
   * validation: forward moves should run the per-step `trigger()` field list and
   * only advance when valid; backward moves are free.
   */
  onStepChange: (nextStep: number) => void | Promise<void>;
  /** Per-step error flags — marks the step card red */
  stepErrors?: Record<number, boolean>;
  /** Localized chrome texts */
  texts: WizardShellTexts;
  /** 'create' shows Submit on the last step; 'edit' shows Save on every step */
  mode?: 'create' | 'edit';
  /** Disables nav buttons and shows loading on the primary action */
  isSaving?: boolean;
  /** Drives the navigation guard (context + beforeunload + guarded Exit) */
  hasUnsavedChanges?: boolean;
  /** Override the default guarded navigate(-1) Exit behavior */
  onExit?: () => void;
  /** Edit-mode primary action (cumulative save of steps up to the current one) */
  onSave?: () => void;
  /** Create-mode primary action on the last step */
  onSubmit?: () => void;
  /** Current step content, rendered by the consumer */
  children: ReactNode;
}

const WizardShell = ({
  steps,
  currentStep,
  onStepChange,
  stepErrors = {},
  texts,
  mode = 'create',
  isSaving = false,
  hasUnsavedChanges = false,
  onExit,
  onSave,
  onSubmit,
  children,
}: WizardShellProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { setHasUnsavedChanges: setGlobalUnsavedChanges } = useNavigationGuard();
  const { confirmUnsavedChanges } = useUnsavedChangesModal();

  // Localized defaults (no hardcoded-English fallbacks — house i18n rule);
  // consumers may still override via `texts` (ProductFormWizard does).
  const stepLabel =
    texts.stepLabel ?? ((stepNumber: number) => t('wizard.stepLabel', { number: stepNumber }));
  const progressLabel =
    texts.progressLabel ??
    ((current: number, total: number) => t('wizard.progressLabel', { current, total }));

  // Guard: propagate unsaved-changes into the global navigation guard,
  // clearing it when the wizard unmounts.
  useEffect(() => {
    setGlobalUnsavedChanges(hasUnsavedChanges);
    return () => {
      setGlobalUnsavedChanges(false);
    };
  }, [hasUnsavedChanges, setGlobalUnsavedChanges]);

  // Guard: browser-level refresh/close protection.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleExit = () => {
    if (onExit) {
      onExit();
      return;
    }
    if (hasUnsavedChanges) {
      confirmUnsavedChanges(() => navigate(-1));
    } else {
      navigate(-1);
    }
  };

  const largeButtonStyle = {
    height: `${controlHeightTokens.controlHeightXL}px`,
  };

  const isLastStep = currentStep === steps.length - 1;

  return (
    <Layout
      style={{
        height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
        backgroundColor: token.colorBgLayout,
        borderTop: `1px solid ${token.colorBorder}`,
      }}
    >
      {/* Sticky Sidebar with Steps */}
      <Sider
        width={SIDER_WIDTH}
        style={{
          backgroundColor: token.colorBgContainer,
          borderRight: `1px solid ${token.colorBorderSecondary}`,
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          position: 'sticky',
          top: `${NAVBAR_HEIGHT}px`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: token.paddingLG,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: `${FOOTER_HEIGHT}px`, // Reserve space for footer alignment
            position: 'relative', // For absolute positioning of progress section
          }}
        >
          {/* Step cards */}
          <div style={{ flex: 1, overflowY: 'auto', paddingBottom: token.paddingMD }}>
            {steps.map((step, index) => {
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              const hasError = stepErrors[index];

              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  onClick={() => onStepChange(index)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onStepChange(index);
                    }
                  }}
                  style={{
                    padding: token.paddingLG,
                    marginBottom: token.marginMD,
                    borderRadius: token.borderRadiusLG,
                    backgroundColor: hasError
                      ? token.colorErrorBg
                      : isCurrent
                        ? token.colorPrimaryBg
                        : isCompleted
                          ? token.colorSuccessBg
                          : token.colorFillQuaternary,
                    border: `2px solid ${
                      hasError
                        ? token.colorError
                        : isCurrent
                          ? token.colorPrimary
                          : isCompleted
                            ? token.colorSuccess
                            : 'transparent'
                    }`,
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                >
                  {/* Status icons in top-right */}
                  {isCompleted && (
                    <div style={{ position: 'absolute', top: 12, right: 12 }}>
                      <CheckCircleOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                    </div>
                  )}
                  {!isCompleted && !isCurrent && (
                    <div style={{ position: 'absolute', top: 12, right: 12 }}>
                      <ClockCircleOutlined
                        style={{ fontSize: 20, color: token.colorTextDisabled }}
                      />
                    </div>
                  )}

                  <div className="tw-flex tw-items-start tw-gap-4">
                    {/* Large icon box */}
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: token.borderRadiusLG,
                        backgroundColor: hasError
                          ? token.colorError
                          : isCompleted
                            ? token.colorSuccess
                            : isCurrent
                              ? token.colorPrimary
                              : token.colorFillTertiary,
                        color:
                          hasError || isCompleted || isCurrent
                            ? token.colorWhite
                            : token.colorTextDisabled,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: 24,
                      }}
                    >
                      {step.icon}
                    </div>

                    {/* Step info */}
                    <div style={{ flex: 1, paddingTop: 2 }}>
                      <div
                        style={{
                          fontSize: token.fontSizeSM,
                          color: token.colorTextSecondary,
                          marginBottom: 4,
                          textTransform: 'uppercase',
                          fontWeight: token.fontWeightStrong,
                          letterSpacing: '0.5px',
                        }}
                      >
                        {stepLabel(index + 1)}
                      </div>
                      <div
                        style={{
                          fontSize: token.fontSize,
                          fontWeight: token.fontWeightStrong,
                          color: hasError
                            ? token.colorError
                            : isCurrent
                              ? token.colorPrimary
                              : isCompleted
                                ? token.colorSuccess
                                : token.colorText,
                          marginBottom: 4,
                        }}
                      >
                        {step.title}
                      </div>
                      {step.description !== undefined && (
                        <div
                          style={{
                            fontSize: token.fontSizeSM,
                            color: token.colorTextSecondary,
                          }}
                        >
                          {step.description}
                        </div>
                      )}

                      {/* Status badge */}
                      <div style={{ marginTop: 8 }}>
                        <Badge
                          status={hasError ? 'error' : isCompleted ? 'success' : 'default'}
                          text={
                            <Text
                              style={{
                                fontSize: token.fontSizeSM,
                                color: hasError
                                  ? token.colorError
                                  : isCompleted
                                    ? token.colorSuccess
                                    : isCurrent
                                      ? token.colorPrimary
                                      : token.colorTextDisabled,
                              }}
                            >
                              {hasError
                                ? texts.status.hasErrors
                                : isCompleted
                                  ? texts.status.completed
                                  : isCurrent
                                    ? texts.status.inProgress
                                    : texts.status.pending}
                            </Text>
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress footer (80px, aligned with the action footer) */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: `${FOOTER_HEIGHT}px`,
              padding: `${token.paddingMD}px ${token.paddingLG}px`,
              backgroundColor: token.colorBgContainer,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              zIndex: 98,
            }}
          >
            <Text
              style={{
                display: 'block',
                fontSize: token.fontSizeSM,
                color: token.colorTextSecondary,
                textAlign: 'center',
              }}
            >
              {progressLabel(currentStep + 1, steps.length)}
            </Text>
            <div
              style={{
                width: '100%',
                height: 4,
                backgroundColor: token.colorBgTextHover,
                borderRadius: token.borderRadiusXS,
                marginTop: token.marginSM,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${((currentStep + 1) / steps.length) * 100}%`,
                  height: '100%',
                  backgroundColor: token.colorPrimary,
                }}
              />
            </div>
          </div>
        </div>
      </Sider>

      {/* Scrollable content area */}
      <Content
        style={{
          backgroundColor: token.colorBgLayout,
          position: 'relative',
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          overflow: 'hidden',
          boxShadow: 'inset 0 4px 8px rgba(0, 0, 0, 0.06), -4px 0 16px rgba(0, 0, 0, 0.08)',
        }}
      >
        <div
          style={{
            height: `calc(100vh - ${NAVBAR_HEIGHT + FOOTER_HEIGHT}px)`,
            overflowY: 'auto',
            padding: token.paddingLG,
          }}
        >
          <div style={{ maxWidth: 1400, margin: '0 auto' }}>{children}</div>
        </div>

        {/* Sticky bottom action bar */}
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: SIDER_WIDTH,
            right: 0,
            height: `${FOOTER_HEIGHT}px`,
            backgroundColor: token.colorBgContainer,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            padding: `${token.paddingMD}px ${token.paddingLG}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 99,
          }}
        >
          {/* Left: Exit (always visible) */}
          <Button danger size="large" onClick={handleExit} style={largeButtonStyle}>
            {texts.exit}
          </Button>

          {/* Right: Previous / Next / primary */}
          <div className="tw-flex tw-gap-3">
            {currentStep > 0 && (
              <Button
                size="large"
                onClick={() => onStepChange(currentStep - 1)}
                style={largeButtonStyle}
                disabled={isSaving}
              >
                {texts.previous}
              </Button>
            )}

            {!isLastStep && (
              <Button
                size="large"
                onClick={() => onStepChange(currentStep + 1)}
                style={largeButtonStyle}
                disabled={isSaving}
              >
                {texts.next}
              </Button>
            )}

            {mode === 'edit'
              ? texts.save !== undefined &&
                onSave && (
                  <Button
                    type="primary"
                    size="large"
                    onClick={onSave}
                    loading={isSaving}
                    style={largeButtonStyle}
                  >
                    {texts.save}
                  </Button>
                )
              : isLastStep &&
                texts.submit !== undefined &&
                onSubmit && (
                  <Button
                    type="primary"
                    size="large"
                    onClick={onSubmit}
                    loading={isSaving}
                    disabled={isSaving}
                    style={largeButtonStyle}
                  >
                    {texts.submit}
                  </Button>
                )}
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default WizardShell;
